import { useInfiniteQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, watch } from 'vue'

import { useI18nAddon } from '@/i18n/i18n'
import { useAppStore } from '@/store/app-store'
import { zipAPIGame } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'

export const MOBILE_FEED_PAGE_SIZE = 48
const FEED_RESTART_STATUSES = [409, 410]

class FeedSessionEnded extends Error {}

export const useMobileFeed = () => {
    const appStore = useAppStore()
    const { currentLocale } = useI18nAddon()
    const { getMobileFeed } = useAPI()

    const platform = computed(() => (appStore.platform === 'ios' ? 'ios' : 'android'))
    const clid = computed(() => `direct-app-${platform.value}`)

    const queryClient = useQueryClient()
    const queryKey = computed(() => ['mobile-feed', platform.value, clid.value, currentLocale.value])

    const query = useInfiniteQuery({
        queryKey,
        queryFn: async ({ pageParam }) => {
            const result = await getMobileFeed({
                platform: platform.value,
                clid: clid.value,
                locale: currentLocale.value,
                pageSize: MOBILE_FEED_PAGE_SIZE,
                ...pageParam,
            })
            if (pageParam.feedId && result.status !== 'ok' && FEED_RESTART_STATUSES.includes(result.httpStatus ?? 0)) {
                throw new FeedSessionEnded()
            }
            if (result.status !== 'ok') {
                throw new Error(result.status === 'error' ? result.message : `HTTP ${result.httpStatus}`)
            }

            const {
                categories: [feed],
                feedId,
                pagination,
            } = result.data
            const games = feed?.games ?? []
            return {
                games: games.map(zipAPIGame),
                positions: games.map((game) => game.position),
                slateId: feed?.slateId,
                totalCount: feed?.totalCount,
                feedId,
                page: pagination.page,
                hasMore: pagination.hasMore,
            }
        },
        initialPageParam: { page: 1 } as { page: number; feedId?: string },
        getNextPageParam: (last) => (last.hasMore ? { page: last.page + 1, feedId: last.feedId } : undefined),
        // The feed is user-specific and must never enter the SSR persister cache.
        persister: undefined,
        // no retries: 409/410 restart the session at once, other errors end the feed
        retry: false,
    })

    watch(
        query.error,
        (error) => {
            if (error instanceof FeedSessionEnded) {
                queryClient.resetQueries({ queryKey: queryKey.value, exact: true })
            }
        },
        { immediate: true },
    )

    return {
        pages: computed(() => query.data.value?.pages ?? []),
        isPending: query.isPending,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isFetchNextPageError: query.isFetchNextPageError,
        fetchNextPage: query.fetchNextPage,
        suspense: query.suspense,
    }
}
